import type { AppointmentDTO } from 'src/types/dto/appointment.dto';

import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';

import { formatRangeFromAppointments } from 'src/sections/visit/components/scheduled';
import { formatAppointmentInstant } from 'src/utils/timezone';

import { getSortedNonBlockerAppointments } from './utils';

interface AppointmentRangeLabelProps {
  appointments: AppointmentDTO[];
  siteTimezoneId?: string;
}

const resolveAppointmentTimezone = (
  appointment: Pick<AppointmentDTO, 'timezoneId'>,
  siteTimezoneId?: string
) => appointment.timezoneId?.trim() || siteTimezoneId?.trim() || 'UTC';

export const AppointmentRangeLabel = memo(
  ({ appointments, siteTimezoneId }: AppointmentRangeLabelProps) => {
    const { t } = useTranslation('activities');
    const sortedAppointments = getSortedNonBlockerAppointments(appointments);
    const label = formatRangeFromAppointments(appointments, siteTimezoneId) || '-';
    const showTooltip = sortedAppointments.length > 0;

    const tooltipTitle = (
      <Stack spacing={0.75} sx={{ py: 0.25, maxWidth: 360 }}>
        {sortedAppointments.map((appointment, index) => {
          const timezoneId = resolveAppointmentTimezone(appointment, siteTimezoneId);

          return (
            <Box key={appointment.id ?? `appointment-${index}`}>
              {index > 0 ? <Divider sx={{ my: 0.75 }} /> : null}
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.35 }}>
                {t('activityModal.details.sections.team.from')}
              </Typography>
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.4 }}>
                {formatAppointmentInstant(appointment.from, timezoneId)}
              </Typography>
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.35 }}>
                {t('activityModal.details.sections.team.to')}
              </Typography>
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.4 }}>
                {formatAppointmentInstant(appointment.to, timezoneId)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );

    return (
      <Tooltip
        title={showTooltip ? tooltipTitle : ''}
        placement="top"
        arrow
        enterDelay={400}
      >
        <Typography
          variant="caption"
          component="span"
          tabIndex={showTooltip ? 0 : -1}
          sx={{
            cursor: showTooltip ? 'help' : 'default',
            borderBottom: showTooltip ? '1px dotted' : 'none',
            borderColor: 'divider',
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {label}
        </Typography>
      </Tooltip>
    );
  }
);

AppointmentRangeLabel.displayName = 'AppointmentRangeLabel';
